import { CONTENT_ONLY } from '@/lib/ui-mode';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { LoginForm } from '@/components/auth/login-form';
import { getLastOrgId, setLastOrgId } from '@/lib/auth-store';
import type { AuthResult } from '@/lib/api/types';

const searchSchema = z.object({
  redirect: z.string().optional(),
});

function LoginPage() {
  const { redirect } = Route.useSearch();
  const navigate = useNavigate();

  function onSuccess(result: AuthResult) {
    if (redirect) {
      void navigate({ href: redirect });
      return;
    }

    const remembered = getLastOrgId();
    const target =
      result.organisations.find((org) => org.id === remembered) ?? result.organisations[0];

    if (!target) {
      void navigate({ to: '/onboarding' });
      return;
    }

    setLastOrgId(target.id);
    void navigate({ to: '/app/$orgId', params: { orgId: target.id } });
  }

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-1.5">
        <span className="section-label">Sign in</span>
        <h1 className="text-title font-semibold tracking-tight text-ink">Welcome back</h1>
        <p className="text-data text-ink-muted">
          {CONTENT_ONLY
            ? 'Sign in to edit the website.'
            : 'Use the email address your organisation invited.'}
        </p>
      </header>

      <LoginForm onSuccess={onSuccess} />

      <div className="flex flex-col gap-2 border-t border-hairline pt-4 text-data text-ink-muted">
        {CONTENT_ONLY ? (
          <p>Need access or a new password? Ask your administrator.</p>
        ) : (
          <>
            <Link to="/forgot-password" className="text-accent hover:underline underline-offset-4">
              Forgot your password?
            </Link>
            <p>
              Need an account?{' '}
              <Link to="/register" className="text-accent hover:underline underline-offset-4">
                Create one
              </Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}

export const Route = createFileRoute('/(auth)/login')({
  validateSearch: searchSchema,
  component: LoginPage,
});
